import { useEffect, useState } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { Building2, ChevronRight, ChevronDown } from 'lucide-react'
import { api, type Room, type PublicOrg, type PublicBuilding } from '../lib/api'
import { useAuthStore } from '../lib/auth'
import { MarketplaceLayout } from '../components/MarketplaceLayout'
import { MarketplaceRoomCard } from '../components/MarketplaceRoomCard'

// Expandable intro text component
function ExpandableIntro({ text }: { text: string }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const shouldTruncate = text.length > 150

  return (
    <div className="mb-8 max-w-3xl">
      <p
        className={`text-base text-gray-600 leading-relaxed whitespace-pre-line ${!isExpanded && shouldTruncate ? 'line-clamp-2' : ''}`}
      >
        {text}
      </p>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          className="mt-2 flex min-h-11 items-center gap-1 text-sm text-gray-900 hover:underline font-semibold"
        >
          {isExpanded ? 'Show less' : 'Show more'}
          <ChevronDown
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      )}
    </div>
  )
}

export function MarketplaceOrgPage() {
  const { orgSlug } = useParams({ from: '/marketplace/$orgSlug' })
  const { checkAuth } = useAuthStore()
  const [organisation, setOrganisation] = useState<PublicOrg | null>(null)
  const [rooms, setRooms] = useState<Room[]>([])
  const [buildings, setBuildings] = useState<Record<string, PublicBuilding>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    loadRooms()
  }, [orgSlug])

  // Update page title when organisation loads
  useEffect(() => {
    if (organisation) {
      document.title = `${organisation.name} - Available Rooms | Roomsi`

      // Set meta description
      const description = organisation.galleryIntro
        ? organisation.galleryIntro.slice(0, 155) + '...'
        : `Book meeting rooms and event spaces at ${organisation.name}. ${rooms.length} rooms available.`
      setMetaTag('description', description)

      // Open Graph tags
      setMetaTag('og:title', `${organisation.name} - Available Rooms`)
      setMetaTag('og:description', description)
      setMetaTag('og:type', 'website')
      setMetaTag('og:url', window.location.href)

      // Find a hero image from first room
      const firstRoomWithImage = rooms.find((r) => r.images && r.images.length > 0)
      const heroImg =
        firstRoomWithImage?.images?.[firstRoomWithImage.heroImageIndex || 0]?.url ||
        firstRoomWithImage?.images?.[0]?.url
      if (heroImg) {
        setMetaTag('og:image', heroImg)
      }

      // Twitter Card tags
      setMetaTag('twitter:card', 'summary_large_image')
      setMetaTag('twitter:title', `${organisation.name} - Available Rooms`)
      setMetaTag('twitter:description', description)
      if (heroImg) {
        setMetaTag('twitter:image', heroImg)
      }

      // Inject Schema.org JSON-LD
      injectOrgSchemaOrg(organisation, rooms, buildings)
    }
    return () => {
      document.title = 'Roomsi'
      const existingSchema = document.querySelector('script[data-schema="org"]')
      if (existingSchema) existingSchema.remove()
    }
  }, [organisation, rooms, buildings])

  const setMetaTag = (name: string, content: string) => {
    const isOg = name.startsWith('og:')
    const isTwitter = name.startsWith('twitter:')
    const attr = isOg || isTwitter ? 'property' : 'name'

    let tag = document.querySelector(`meta[${attr}="${name}"]`)
    if (!tag) {
      tag = document.createElement('meta')
      tag.setAttribute(attr, name)
      document.head.appendChild(tag)
    }
    tag.setAttribute('content', content)
  }

  const injectOrgSchemaOrg = (
    org: PublicOrg,
    rooms: Room[],
    buildings: Record<string, PublicBuilding>
  ) => {
    // Remove existing schema
    const existingSchema = document.querySelector('script[data-schema="org"]')
    if (existingSchema) existingSchema.remove()

    // Get first building for address
    const firstBuilding = Object.values(buildings)[0]

    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: org.name,
      description:
        org.galleryIntro ||
        `${org.name} offers ${rooms.length} bookable meeting rooms and event spaces.`,
      url: window.location.href,
    }

    // Add address if available
    if (firstBuilding) {
      schema.address = {
        '@type': 'PostalAddress',
        streetAddress: firstBuilding.address,
      }
    }

    // Add rooms as offers
    if (rooms.length > 0) {
      schema.makesOffer = rooms.slice(0, 10).map((room) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'MeetingRoom',
          name: room.name,
          description:
            room.descriptionHtml?.replace(/<[^>]*>/g, '').slice(0, 200) ||
            `Meeting room at ${org.name}`,
          url: `${window.location.origin}/marketplace/${org.slug}/${room.slug}`,
          maximumAttendeeCapacity: room.capacity,
        },
        price: room.hourlyRate,
        priceCurrency: org.currency,
      }))
    }

    // Add images from rooms
    const allImages = rooms.flatMap((r) => r.images?.map((img) => img.url) || []).slice(0, 5)
    if (allImages.length > 0) {
      schema.image = allImages
    }

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-schema', 'org')
    script.textContent = JSON.stringify(schema)
    document.head.appendChild(script)
  }

  const loadRooms = async () => {
    try {
      setIsLoading(true)
      setError('')
      const data = await api.public.getRooms(orgSlug)
      setOrganisation(data.organisation)
      setRooms(data.rooms)
      setBuildings(data.buildings || {})
    } catch (err: unknown) {
      if (err instanceof Error && 'code' in err && err.code === 'NOT_FOUND') {
        setError('Organisation not found')
      } else {
        setError('Failed to load rooms')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{error}</h1>
          <p className="text-gray-500 mb-4">The organisation you're looking for doesn't exist.</p>
          <Link to="/marketplace" className="text-primary-600 hover:text-primary-700">
            Browse all spaces
          </Link>
        </div>
      </div>
    )
  }

  return (
    <MarketplaceLayout returnTo={`/marketplace/${orgSlug}`}>
      {/* Main Content */}
      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 mb-12">
        {/* Back to marketplace link */}
        <div className="mb-4 sm:mb-6">
          <Link
            to="/marketplace"
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to all rooms
          </Link>
        </div>

        <h1 className="text-3xl sm:text-[40px] leading-tight font-extrabold text-gray-900 mb-6 break-words">{organisation?.name}</h1>
        {organisation?.galleryIntro && <ExpandableIntro text={organisation.galleryIntro} />}

        {rooms.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No rooms available</h2>
            <p className="text-gray-500">This organisation hasn't published any rooms yet.</p>
          </div>
        ) : (
          <>
            <div className="mb-4 lg:mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Available rooms</h2>
              <p className="text-gray-600 mt-1 text-sm">
                {rooms.length} room{rooms.length !== 1 ? 's' : ''} available for booking
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {rooms.map((room) => (
                <MarketplaceRoomCard
                  key={room.id}
                  room={{
                    ...room,
                    orgSlug,
                    orgName: organisation?.name || orgSlug,
                    currency: organisation?.currency,
                    buildingName: room.buildingId ? buildings[room.buildingId]?.name : undefined,
                    image: room.images?.[room.heroImageIndex || 0]?.url || room.images?.[0]?.url,
                    location: (room.buildingId ? buildings[room.buildingId]?.address : null) || room.location,
                    accessibilityFeatureCount: room.accessibilityFeatures?.length,
                  }}
                />
              ))}
            </div>
          </>
        )}
      </main>

    </MarketplaceLayout>
  )
}
